import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, Subject, of } from 'rxjs';
import { SignalRService } from './signalr.service';
import { Notification } from '../models/notification.model';
import { ApiResponse } from './auth.service';
import { HttpService } from './http.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_LOADING } from '../interceptors/loader.interceptor';
import { AuthService } from './auth.service'; // Added AuthService import

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private notifications = new BehaviorSubject<Notification[]>([]);
    private unreadCount = new BehaviorSubject<number>(0);
    private refreshTrigger = new Subject<void>();

    public notifications$ = this.notifications.asObservable();
    public unreadCount$ = this.unreadCount.asObservable();
    public refreshTrigger$ = this.refreshTrigger.asObservable();

    constructor(
        private http: HttpService,
        private signalRService: SignalRService,
        private httpClient: HttpClient,
        private authService: AuthService // Added AuthService injection
    ) {
        this.initializeService();
    }

    /**
     * Initialize the notification service
     */
    private initializeService(): void {
        // Subscribe to SignalR notifications
        this.signalRService.notificationReceived$.subscribe(notification => {
            this.handleIncomingNotification(notification);
        });

    }

    /**
     * Handle incoming notifications from SignalR
     */
    private handleIncomingNotification(notification: Notification): void {
        const currentUser = this.authService.getCurrentUser();
        const currentNotifications = this.notifications.value;

        // Only process notification if it's for the current user
        if (currentUser?.id && notification.recipientUserId !== currentUser.id) {
            return;
        }

        // Check if notification already exists to prevent duplicates
        const notificationExists = currentNotifications.some(n => n.id === notification.id);

        if (!notificationExists) {
            const updatedNotifications = [notification, ...currentNotifications];
            this.notifications.next(updatedNotifications);

            // Update the unread count based on the new notifications array
            this.updateUnreadCount(updatedNotifications);
        } else {
            // If notification exists, still update the count in case read status changed
            this.updateUnreadCount();
        }
    }


    /**
     * Load notifications and update local state (filtered by current user)
     */
    public loadNotifications(): Observable<any> {
        return this.getAllNotifications({
            page: 0,
            pageSize: 20,
            sortColumn: "createdDate",
            sortDirection: "desc"
        }).pipe(
            map(response => {
                if (response && response.items) {
                    const currentUser = this.authService.getCurrentUser();

                    // Filter notifications for current user only
                    let filteredNotifications = response.items;
                    if (currentUser?.id) {
                        filteredNotifications = response.items.filter(
                            (n: Notification) => n.recipientUserId === currentUser.id
                        );
                    }

                    this.notifications.next(filteredNotifications);
                    this.updateUnreadCount(filteredNotifications);
                }
                return response;
            })
        );
    }

    /**
     * Get all notifications from API with pagination, sorting, and filtering
     */
    public getAllNotifications(payload?: {
        page?: number;
        pageSize?: number;
        sortColumn?: string;
        sortDirection?: string;
        search?: string;
        filters?: Array<{ field: string; value: string }>;
    }): Observable<any> {
        const defaultPayload = {
            page: 0,
            pageSize: 10,
            sortColumn: "createdDate",
            sortDirection: "desc",
            search: "",
            filters: [],
        };

        const requestPayload = { ...defaultPayload, ...payload };
        const context = new HttpContext().set(SKIP_LOADING, true);

        // return this.http.post(endpoint, params, { context });
        return this.httpClient.post<ApiResponse>('/Notification/get-all-notification', requestPayload, { context })
            .pipe(
                map(response => {
                    if (response.isSuccess && response.data) {
                        return response.data;
                    }
                    return { items: [], totalRecord: 0, page: 0, pageSize: 10 };
                })
            );
    }

    /**
     * Mark notification as read (only if current user is the recipient)
     */
    public markAsRead(notificationId: string): Observable<boolean> {
        const currentUser = this.authService.getCurrentUser();
        const currentNotifications = this.notifications.value;
        
        // Find the notification to verify ownership
        const notification = currentNotifications.find(n => n.id === notificationId);
        
        // Check if current user is the recipient of this notification
        if (!currentUser?.id || !notification || notification.recipientUserId !== currentUser.id) {
            console.warn('Unauthorized attempt to mark notification as read:', {
                notificationId,
                currentUserId: currentUser?.id,
                recipientUserId: notification?.recipientUserId
            });
            return of(false);
        }

        return this.http.post<ApiResponse<boolean>>(`Notification/read-notification/${notificationId}`, {})
            .pipe(
                map(response => {
                    if (response.isSuccess) {
                        const updatedNotifications = currentNotifications.map(n =>
                            n.id === notificationId ? { ...n, isRead: true } : n
                        );
                        this.notifications.next(updatedNotifications);
                        this.updateUnreadCount();
                        this.triggerRefresh();

                        return true;
                    }
                    return false;
                })
            );
    }

    /**
     * Mark all notifications as read (only for current user)
     */
    public markAllAsRead(): Observable<boolean> {
        const currentUser = this.authService.getCurrentUser();
        
        if (!currentUser?.id) {
            console.warn('No current user found, cannot mark all notifications as read');
            return of(false);
        }

        return this.http.post<ApiResponse<boolean>>(`Notification/read-all-notification/${currentUser.id}`,{})
            .pipe(
                map(response => {
                    if (response.isSuccess) {
                        const currentNotifications = this.notifications.value;
                        // Only mark notifications as read for the current user
                        const updatedNotifications = currentNotifications.map(n => 
                            n.recipientUserId === currentUser.id ? { ...n, isRead: true } : n
                        );
                        this.notifications.next(updatedNotifications);
                        this.updateUnreadCount();
                        return true;
                    }
                    return false;
                })
            );
    }


    /**
     * Update unread count based on recipient user ID
     */
    public updateUnreadCount(notifications?: Notification[]): void {
        const currentUser = this.authService.getCurrentUser();
        const notificationsArray = notifications || this.notifications.value;

        // Filter notifications to only show those for the current user
        let filteredNotifications = [...notificationsArray];

        if (currentUser?.id) {
            // All users should only see their own notifications based on recipientUserId
            filteredNotifications = filteredNotifications.filter(
                n => n.recipientUserId === currentUser.id
            );
        } else {
            console.warn('No current user ID found, showing all notifications');
            // If no user is logged in, show no notifications
            filteredNotifications = [];
        }

        // Then count unread from the filtered list
        const unreadCount = filteredNotifications.filter(n => !n.isRead).length;

        // Force the BehaviorSubject to emit even if the value is the same
        this.unreadCount.next(unreadCount);
    }

    /**
     * Trigger refresh for notification table
     */
    public triggerRefresh(): void {
        this.refreshTrigger.next();
    }
}
